import { BreadcrumbItem } from '@zeus/core/layout/MainLayout';
import Loader from '@zeus/core/Loader';
import { MetricsGraphs, MetricsTS } from '_/components/common/MetricsGraphs/MetricsGraphs';
import { useCollection } from '_/contexts/CollectionContext';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import { useSnapshotContext } from '_/contexts/SnapshotContext';
import { useSourceContext } from '_/contexts/SourceContext';
import zeusSdk from '_/utils/SDKs';
import React, { ReactElement, useEffect, useState } from 'react';

function SnapshotGraphs(): ReactElement {
  const { selectedOrgSlug, selectedOrganization } = useOrganization();
  const { selectedProjectSlug, selectedProject } = useProject();
  const { selectedCollectionSlug, selectedCollection } = useCollection();
  const { selectedSourceSlug, selectedSource } = useSourceContext();
  const { snapshotId, isLoading: isLoadingSnapshot } = useSnapshotContext();
  const { setBreadcrumbs } = useTopHeader();
  const [metricsTS, setMetricsTS] = useState<MetricsTS | null>(null);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const breadcrumbs: BreadcrumbItem[] = [
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${selectedOrgSlug}` },
      { title: 'Projects', href: `/orgs/${selectedOrgSlug}/projects` },
      {
        title: selectedProject?.name || '',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/home`,
      },
      {
        title: 'Collections',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections`,
      },
      {
        title: selectedCollection?.name || '',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedCollectionSlug}/home`,
      },
      {
        title: selectedSource?.name || '',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources/${selectedSourceSlug}/home`,
      },
      {
        title: 'Snapshots',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources/${selectedSourceSlug}/snapshots`,
      },
      { title: 'Graphs' },
    ];

    setBreadcrumbs(breadcrumbs);

    return () => {
      setBreadcrumbs([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedOrganization?.name,
    selectedOrgSlug,
    selectedProject?.name,
    selectedProjectSlug,
    selectedCollection?.name,
    selectedCollectionSlug,
    selectedSource?.name,
    selectedSourceSlug,
    snapshotId,
  ]);

  useEffect(() => {
    async function fetchMetrics() {
      if (!snapshotId) {
        setIsLoadingMetrics(false);
        return;
      }

      setIsLoadingMetrics(true);
      setError(null);

      try {
        const response = await zeusSdk.snapshots.getMetricsTimeseries(snapshotId);
        setMetricsTS(response);
        setError(null);
      } catch (err: any) {
        console.error('Error fetching metrics:', err);
        setError(err.message || 'Failed to fetch metrics data');
        setMetricsTS(null);
      } finally {
        setIsLoadingMetrics(false);
      }
    }

    void fetchMetrics();
  }, [snapshotId]);

  if (isLoadingSnapshot || isLoadingMetrics) {
    return <Loader />;
  }

  return <MetricsGraphs metricsTS={metricsTS} error={error} />;
}

export default SnapshotGraphs;
